import { useEffect, useRef, useState } from "react";
import { Minus, X } from "lucide-react";
import { Label } from "@/components/ui/label";
import SearchableSelect from "@/components/SearchableSelect";
import RichEmailComposer from "@/components/RichEmailComposer";
import api from "@/lib/api";
import { useCompose } from "@/contexts/ComposeContext";
import { toast } from "sonner";

// Iter 68 — Single mount point for ALL compose sessions. Reads from the
// ComposeContext and renders:
//   • The expanded session (if any) as a full floating panel anchored
//     bottom-right.
//   • Each minimised session as a pill anchored bottom-right, stacked
//     leftwards next to (or replacing) the expanded panel.
//
// Up to 3 sessions can exist concurrently. The pill row stays compact
// even at 3.

const PANEL_W = 820;
const PILL_W = 280;
const GAP = 12;
const BASE_RIGHT = 24;

export default function FloatingComposeRoot() {
  const { sessions, minimise, expand, close, update, open } = useCompose();
  const expanded = sessions.find((s) => !s.minimized);
  const minimised = sessions.filter((s) => s.minimized);

  // Iter 85 — Undo-send bridge. RichEmailComposer calls
  // `window.__closeUndoSend({ snapshot, commit })` on Send: we show a
  // sonner toast with an Undo action that survives composer close, and
  // either fire the real Gmail send after 5s or re-open the composer
  // with the captured state if Undo is clicked.
  //
  // Iter 86 — Keep `open` in a ref so we don't have to redeclare the
  // bridge on every context re-render. Previously the useEffect deps
  // `[open]` caused the cleanup to fire when a session opened/closed
  // (the context `open` is wrapped in useCallback whose identity changes
  // with `expandedId`), which CLEARED the pending undo timer and the
  // email was never sent.
  const openRef = useRef(open);
  useEffect(() => { openRef.current = open; }, [open]);

  useEffect(() => {
    let pendingCommit = null;
    let pendingTimer = null;
    let lastSnapshot = null;

    const reset = () => {
      if (pendingTimer) clearTimeout(pendingTimer);
      pendingTimer = null;
      pendingCommit = null;
      lastSnapshot = null;
    };

    const fireUndo = () => {
      if (!lastSnapshot) return;
      const snap = lastSnapshot;
      reset();
      openRef.current?.({
        initial: {
          to: snap.to, cc: snap.cc, bcc: snap.bcc,
          subject: snap.subject,
          body_html: snap.body_html,
          thread_id: snap.thread_id,
          in_reply_to: snap.in_reply_to,
          references: snap.references,
          original_message_id: snap.original_message_id,
          compose_mode: snap.compose_mode,
        },
        clientId: snap.tagClientId || "",
        showClientPicker: true,
        title: snap.subject || "Recovered draft",
      });
      toast.info("Send cancelled. The draft is back open for editing.");
    };

    window.__closeUndoSend = ({ snapshot, commit }) => {
      // If a previous send is still in its undo window, commit it
      // immediately so we don't drop it.
      if (pendingCommit) {
        try { pendingCommit(); } catch (_e) { /* swallow */ }
        reset();
      }
      pendingCommit = commit;
      lastSnapshot = snapshot;
      toast.success("Sent.", {
        description: "Click Undo within 5 seconds to keep editing.",
        action: { label: "Undo", onClick: fireUndo },
        duration: 5000,
      });
      pendingTimer = setTimeout(() => {
        try { pendingCommit?.(); } catch (_e) { /* swallow */ }
        reset();
      }, 5000);
    };
    return () => {
      // Commit anything still pending before tearing down — otherwise a
      // hot-reload or full unmount drops the email.
      if (pendingCommit) {
        try { pendingCommit(); } catch (_e) { /* swallow */ }
      }
      reset();
      try { delete window.__closeUndoSend; } catch (_e) { /* ignore */ }
    };
    // Intentionally [] — see openRef above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (sessions.length === 0) return null;
  return (
    <>
      {/* Expanded panel — only one at a time */}
      {expanded && (
        <ExpandedPanel
          key={expanded.id}
          session={expanded}
          onMinimise={() => minimise(expanded.id)}
          onClose={() => close(expanded.id)}
          onUpdate={(patch) => update(expanded.id, patch)}
        />
      )}
      {/* Minimised pills — laid out right-to-left */}
      {minimised.map((s, idx) => {
        const offsetRight = (expanded ? PANEL_W + GAP : 0) + idx * (PILL_W + GAP) + BASE_RIGHT;
        return (
          <button
            key={s.id}
            type="button"
            data-testid={`compose-pill-${s.id}`}
            onClick={() => expand(s.id)}
            style={{ right: offsetRight }}
            className="fixed bottom-0 w-[280px] bg-slate-100 border border-slate-300 rounded-t-lg shadow-lg z-[60] flex items-center gap-2 px-4 py-2.5 hover:bg-slate-200 text-left"
            title="Restore draft"
          >
            <span className="text-slate-700 font-medium text-[13px] truncate flex-1">
              {s.title || "New message"}
            </span>
            <span
              role="button"
              title="Discard draft"
              data-testid={`compose-pill-close-${s.id}`}
              onClick={(e) => { e.stopPropagation(); close(s.id); }}
              className="text-slate-500 hover:text-slate-800 p-1 rounded hover:bg-slate-300"
            >
              <X className="h-4 w-4"/>
            </span>
          </button>
        );
      })}
    </>
  );
}

// One expanded session = one floating panel.
function ExpandedPanel({ session, onMinimise, onClose, onUpdate }) {
  const { id, initial, title, showClientPicker, clientId, onSent } = session;
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState(clientId || initial?.client_id || "");
  const [gmailConnected, setGmailConnected] = useState(true);
  // Iter 84 — Audit-trail token. Fetched once on mount (when we have a
  // task_id) and re-fetched whenever the client picker changes the
  // client tag. The chip rendered inside the composer is purely visual —
  // the backend interceptor is the source of truth at send time.
  const [auditToken, setAuditToken] = useState(null);

  useEffect(() => {
    if (showClientPicker) {
      api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
    }
    api.get("/gmail/status").then((r) => setGmailConnected(!!r.data?.connected)).catch(() => {});
  }, [showClientPicker]);

  // Iter 84 — Resolve the audit token whenever the task_id OR client
  // tag changes. Task always wins (priority spec: T > C > none).
  useEffect(() => {
    const taskId = initial?.task_id || "";
    const params = new URLSearchParams();
    if (taskId) params.set("task_id", taskId);
    else if (client) params.set("client_id", client);
    if (!params.toString()) { setAuditToken(null); return; }
    let active = true;
    api.get(`/audit-token?${params.toString()}`)
      .then((r) => { if (active) setAuditToken(r.data?.token ? r.data : null); })
      .catch(() => { if (active) setAuditToken(null); });
    return () => { active = false; };
  }, [client, initial?.task_id]);

  // Push the latest subject into the session title so the pill reflects
  // what the user typed (Gmail behaviour).
  const handleSubjectChange = (subject) => {
    onUpdate({ title: subject || "New message" });
  };

  return (
    <div
      data-testid={`compose-panel-${id}`}
      style={{ right: BASE_RIGHT, width: PANEL_W }}
      className="fixed bottom-0 max-w-[96vw] h-[min(80vh,640px)] bg-white border border-slate-300 rounded-t-lg shadow-2xl z-[60] flex flex-col"
    >
      <div className="bg-slate-100 border-b border-slate-200 rounded-t-lg px-4 py-2.5 shrink-0 flex items-center gap-2">
        <span className="text-slate-700 font-medium text-[13px] truncate flex-1">{title || "New message"}</span>
        <button type="button" data-testid={`compose-minimize-${id}`} title="Minimise"
          onClick={onMinimise}
          className="text-slate-500 hover:text-slate-800 p-1 rounded hover:bg-slate-200">
          <Minus className="h-4 w-4"/>
        </button>
        <button type="button" data-testid={`compose-close-${id}`} title="Discard"
          onClick={onClose}
          className="text-slate-500 hover:text-slate-800 p-1 rounded hover:bg-slate-200">
          <X className="h-4 w-4"/>
        </button>
      </div>

      {showClientPicker && (
        <div className="flex items-center gap-3 px-4 py-2 border-b border-slate-200 bg-white shrink-0">
          <Label className="text-[11px] uppercase tracking-wider text-slate-400 w-12 shrink-0">Client</Label>
          <SearchableSelect
            value={client}
            onValueChange={setClient}
            options={clients.map((c) => ({ value: c.client_id, label: c.alias ? `${c.name} (${c.alias})` : c.name }))}
            emptyLabel="No client"
            placeholder="No client"
            testId={`compose-client-${id}`}
            className="border-none px-0"
          />
          <div className="ml-auto text-[11px] text-slate-500">
            {gmailConnected ? <span className="text-emerald-600">Sending via your Gmail</span> : <span>Gmail not connected</span>}
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-hidden">
        <RichEmailComposer
          initial={{
            to: initial?.to || initial?.to_email || "",
            cc: initial?.cc || "",
            bcc: initial?.bcc || "",
            subject: initial?.subject || "",
            body_html: initial?.body_html || "",
            thread_id: initial?.thread_id || null,
            in_reply_to: initial?.in_reply_to || "",
            references: initial?.references || "",
            task_id: initial?.task_id || null,
            original_message_id: initial?.original_message_id || "",
            compose_mode: initial?.compose_mode || "",
            quote_html: initial?.quote_html || "",
            // Iter 82 — forward attachments REFS (parent gmail_id +
            // attachment_id, no base64). The composer renders them as
            // removable chips and the backend hydrates the bytes from
            // Gmail at send time.
            forward_attachments: initial?.forward_attachments || [],
          }}
          gmailConnected={gmailConnected}
          onSubjectChange={handleSubjectChange}
          draftId={initial?.draft_id || ""}
          onDraftSaved={() => { /* Drafts sidebar listens for own refresh */ }}
          onCancel={onClose}
          onSent={() => { onSent?.(); onClose(); }}
          auditToken={auditToken}
          tagClientId={client || ""}
        />
      </div>
    </div>
  );
}
